import React, { useState } from "react";
import { FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Hide, Show } from "@/assets/icons/icons";
import { FormField } from "@/components/Interface/FormFields/FormField/FormField";
import type { FormFieldProps } from "@/components/Interface/FormFields/FormField/FormField.utils";

/**
 * Text input that toggles between password and plain-text mode via a
 * Show/Hide button in the right icon slot. Wraps FormField so it inherits
 * all layout, validation, and styling behaviour from the shared form system.
 */
export const PasswordField = <TFormValues extends FieldValues>(
  props: React.PropsWithChildren<FormFieldProps<TFormValues>>,
) => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const isDisabled = !!props.options?.disabled;

  const icon = (
    <button
      type="button"
      className="flex shrink-0 items-center focus:outline-none"
      onClick={() => setShowPassword((v) => !v)}
      aria-label={
        showPassword
          ? t("translation:label.hidePassword")
          : t("translation:label.showPassword")
      }
      disabled={isDisabled}
    >
      {showPassword ? (
        <Hide
          width={24}
          height={24}
          className="text-foreground-primary"
          data-testid="hide-password-icon"
        />
      ) : (
        <Show
          width={24}
          height={24}
          className="text-foreground-primary"
          data-testid="show-password-icon"
        />
      )}
    </button>
  );

  return (
    <FormField
      {...props}
      type={showPassword ? "text" : "password"}
      icon={icon}
    />
  );
};
